import { Component, OnInit } from '@angular/core';
import { AuthService } from '../service/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import jwtDecode from 'jwt-decode';

export interface TrustedDeviceItem {
  login_id: number;
  device_type: string;
  browser: string;
  os: string;
  ip_address: string;
  city: string | null;
  country: string | null;
  created_at: string;
  trusted_at: string | null;
  is_active: boolean;
  is_trusted: boolean;
  trust_expires_at?: string | null;
  user_agent?: string | null;
  token_fingerprint?: string | null;
  is_suspicious?: boolean;
  suspicious_reason?: string | null;
  logged_out_at?: string | null;
}

@Component({
  selector: 'app-trusted-devices',
  templateUrl: './trusted-devices.component.html',
  styleUrls: ['./trusted-devices.component.scss'],
})
export class TrustedDevicesComponent implements OnInit {
  curentLang: string = '';
  devices: TrustedDeviceItem[] = [];
  currentLoginId: number | null = null;
  canMarkCurrentTrusted = false;
  loading = false;
  trustDurationDays = 30;

  constructor(
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'en';
  }

  /** Translated "location not available" with fallback so the raw key never shows */
  get locationNotAvailableText(): string {
    const key = 'TrustedDevices.locationNotAvailable';
    const t = this.translate.instant(key);
    if (t && t !== key) return t;
    return this.curentLang === 'ar' ? 'غير متوفر (IP محلي)' : (this.curentLang === 'he' ? 'לא זמין (IP מקומי)' : 'Not available (local IP)');
  }

  ngOnInit(): void {
    this.setCurrentLoginId();
    this.loadDevices();
  }

  private setCurrentLoginId(): void {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded: any = jwtDecode(token);
        this.currentLoginId = decoded?.loginId ?? null;
      }
    } catch {
      this.currentLoginId = null;
    }
  }

  loadDevices(): void {
    this.loading = true;
    this.authService.getTrustedDevices().subscribe({
      next: (res) => {
        // Handle all possible response shapes:
        // { data: [...] }, { sessions: [...] }, { devices: [...] }, or the array directly
        const raw =
          Array.isArray(res) ? res :
          Array.isArray(res?.data) ? res.data :
          Array.isArray(res?.sessions) ? res.sessions :
          Array.isArray(res?.devices) ? res.devices :
          null;

        if (raw) {
          this.devices = raw.map((d: any) => ({ ...d, is_trusted: !!d.is_trusted }));
          if (res?.trust_duration_days != null) this.trustDurationDays = res.trust_duration_days;
          const currentDevice = this.devices.find((d) => d.login_id === this.currentLoginId);
          this.canMarkCurrentTrusted = !!currentDevice && !currentDevice.is_trusted;
        } else {
          this.devices = [];
          this.canMarkCurrentTrusted = this.currentLoginId != null;
        }
      },
      error: (err) => {
        const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.messageHe
          || (this.curentLang === 'ar' ? 'حدث خطأ في تحميل الأجهزة.' : 'Failed to load devices.');
        Swal.fire({ icon: 'error', title: msg });
        this.devices = [];
      },
      complete: () => (this.loading = false),
    });
  }

  markAsTrusted(device: TrustedDeviceItem): void {
    if (!this.isCurrentDevice(device)) return;
    this.spinner.show();
    this.authService.markCurrentDeviceTrusted().subscribe({
      next: (res) => {
        if (res?.status === 200) {
          const msg = this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'he' ? res.messageHe : res.messageEn);
          Swal.fire({ icon: 'success', title: msg || 'Device marked as trusted.' });
          const d = this.devices.find((x) => x.login_id === device.login_id);
          if (d) {
            d.is_trusted = true;
            d.trusted_at = new Date().toISOString();
          }
          this.canMarkCurrentTrusted = false;
        } else {
          const msg = this.curentLang === 'ar' ? res?.messageAr : (this.curentLang === 'he' ? res?.messageHe : res?.messageEn);
          Swal.fire({ icon: 'warning', title: msg || 'Could not mark device as trusted.' });
        }
      },
      error: (err) => {
        const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.messageHe
          || (this.curentLang === 'ar' ? 'حدث خطأ.' : 'An error occurred.');
        Swal.fire({ icon: 'error', title: msg });
      },
      complete: () => this.spinner.hide(),
    });
  }

  /** Mark current device as trusted (used when current device is not in list yet) */
  markCurrentTrusted(): void {
    this.spinner.show();
    this.authService.markCurrentDeviceTrusted().subscribe({
      next: (res) => {
        if (res?.status === 200) {
          const msg = this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'he' ? res.messageHe : res.messageEn);
          Swal.fire({ icon: 'success', title: msg || 'Device marked as trusted.' });
          this.loadDevices();
        } else {
          const msg = this.curentLang === 'ar' ? res?.messageAr : (this.curentLang === 'he' ? res?.messageHe : res?.messageEn);
          Swal.fire({ icon: 'warning', title: msg || 'Could not mark device as trusted.' });
        }
      },
      error: (err) => {
        const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.messageHe
          || (this.curentLang === 'ar' ? 'حدث خطأ.' : 'An error occurred.');
        Swal.fire({ icon: 'error', title: msg });
      },
      complete: () => this.spinner.hide(),
    });
  }

  revoke(device: TrustedDeviceItem): void {
    const title = this.translate.instant('TrustedDevices.revokeConfirmTitle');
    Swal.fire({
      icon: 'question',
      title: title || 'Remove trust from this device?',
      showCancelButton: true,
      confirmButtonText: this.curentLang === 'ar' ? 'نعم، إلغاء الثقة' : (this.curentLang === 'he' ? 'כן, הסר אמון' : 'Yes, remove trust'),
      cancelButtonText: this.translate.instant('TrustedDevices.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.authService.revokeTrustedDevice(device.login_id).subscribe({
          next: (res) => {
            if (res?.status === 200) {
              const msg = this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'he' ? res.messageHe : res.messageEn);
              Swal.fire({ icon: 'success', title: msg || 'Trust removed.' });
              const d = this.devices.find((x) => x.login_id === device.login_id);
              if (d) {
                d.is_trusted = false;
                d.trusted_at = null;
              }
              this.canMarkCurrentTrusted = this.currentLoginId != null && !this.devices.some((d) => d.login_id === this.currentLoginId && d.is_trusted);
            } else {
              const msg = this.curentLang === 'ar' ? res?.messageAr : (this.curentLang === 'he' ? res?.messageHe : res?.messageEn);
              Swal.fire({ icon: 'warning', title: msg || 'Could not remove trust.' });
            }
          },
          error: (err) => {
            const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.messageHe
              || (this.curentLang === 'ar' ? 'حدث خطأ.' : 'An error occurred.');
            Swal.fire({ icon: 'error', title: msg });
          },
          complete: () => this.spinner.hide(),
        });
      }
    });
  }

  forceLogout(device: TrustedDeviceItem): void {
    const isCurrent = this.isCurrentDevice(device);
    const title = this.translate.instant('TrustedDevices.forceLogoutConfirmTitle');
    const text = isCurrent
      ? (this.translate.instant('TrustedDevices.forceLogoutConfirmCurrent') || 'You will be logged out from this device.')
      : (this.translate.instant('TrustedDevices.forceLogoutConfirmOther') || 'That device will be logged out.');
    Swal.fire({
      icon: 'warning',
      title: title || (this.curentLang === 'ar' ? 'تسجيل الخروج من هذا الجهاز؟' : (this.curentLang === 'he' ? 'להתנתק מהמכשיר הזה?' : 'Force log out this device?')),
      text,
      showCancelButton: true,
      confirmButtonText: this.curentLang === 'ar' ? 'نعم، تسجيل الخروج' : (this.curentLang === 'he' ? 'כן, התנתק' : 'Yes, log out'),
      cancelButtonText: this.curentLang === 'ar' ? 'إلغاء' : (this.curentLang === 'he' ? 'ביטול' : 'Cancel'),
      confirmButtonColor: '#dc3545',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.authService.terminateSession(device.login_id).subscribe({
          next: (res) => {
            if (res?.status === 200) {
              const msg = this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'he' ? res.messageHe : res.messageEn);
              Swal.fire({ icon: 'success', title: msg || 'Session ended.' });
              if (isCurrent) {
                this.authService.logout();
              } else {
                this.devices = this.devices.filter((d) => d.login_id !== device.login_id);
                this.canMarkCurrentTrusted = this.currentLoginId != null && !this.devices.some((d) => d.login_id === this.currentLoginId && d.is_trusted);
              }
            } else {
              const msg = this.curentLang === 'ar' ? res?.messageAr : (this.curentLang === 'he' ? res?.messageHe : res?.messageEn);
              Swal.fire({ icon: 'warning', title: msg || 'Could not end session.' });
            }
          },
          error: (err) => {
            const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.messageHe
              || (this.curentLang === 'ar' ? 'حدث خطأ.' : 'An error occurred.');
            Swal.fire({ icon: 'error', title: msg });
          },
          complete: () => this.spinner.hide(),
        });
      }
    });
  }

  isCurrentDevice(device: TrustedDeviceItem): boolean {
    return this.currentLoginId != null && device.login_id === this.currentLoginId;
  }

  /** User-friendly date and time (e.g. "16 Feb 2026, 2:00 pm") */
  formatDate(value: string | null): string {
    if (!value) return '—';
    try {
      const d = new Date(value);
      const locale = this.curentLang === 'ar' ? 'ar' : (this.curentLang === 'he' ? 'he' : 'en');
      return d.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return value;
    }
  }

  deviceLabel(device: TrustedDeviceItem): string {
    const parts = [device.browser || '', device.device_type || '', device.os || ''].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
  }

  /** User-friendly device summary: "Google Chrome on macOS (Desktop)" */
  deviceFriendlyLabel(device: TrustedDeviceItem): string {
    const browser = this.friendlyBrowserName(device.browser);
    const os = (device.os || '').trim() || null;
    const type = (device.device_type || '').trim().toLowerCase();
    const typeLabel = type ? `(${type.charAt(0).toUpperCase() + type.slice(1)})` : '';
    if (browser && os) return `${browser} on ${os} ${typeLabel}`.trim();
    if (browser) return typeLabel ? `${browser} ${typeLabel}`.trim() : browser;
    return this.deviceLabel(device);
  }

  friendlyBrowserName(browser: string | null | undefined): string {
    if (!browser || !browser.trim()) return '—';
    const b = browser.trim();
    if (/chrome/i.test(b) && !/edge/i.test(b)) return 'Google Chrome';
    if (/firefox/i.test(b)) return 'Firefox';
    if (/safari/i.test(b) && !/chrome/i.test(b)) return 'Safari';
    if (/edge/i.test(b)) return 'Microsoft Edge';
    return b;
  }

  /** Parse User-Agent into a short readable line, e.g. "Chrome 144 on macOS" */
  userAgentFriendly(ua: string | null | undefined): string | null {
    if (!ua || !ua.trim()) return null;
    const u = ua.trim();
    let browser = '';
    let version = '';
    let os = '';
    if (/Chrome\/[\d.]+/.test(u) && !/Edge|Edg\//.test(u)) {
      const m = u.match(/Chrome\/([\d.]+)/);
      browser = 'Chrome';
      version = m ? m[1].split('.')[0] : '';
    } else if (/Firefox\/[\d.]+/.test(u)) {
      const m = u.match(/Firefox\/([\d.]+)/);
      browser = 'Firefox';
      version = m ? m[1].split('.')[0] : '';
    } else if (/Safari\/[\d.]+/.test(u) && !/Chrome/.test(u)) {
      const m = u.match(/Version\/([\d.]+)/);
      browser = 'Safari';
      version = m ? m[1].split('.')[0] : '';
    } else if (/Edg\//.test(u)) {
      const m = u.match(/Edg\/([\d.]+)/);
      browser = 'Edge';
      version = m ? m[1].split('.')[0] : '';
    }
    if (/Windows NT/.test(u)) os = 'Windows';
    else if (/Mac OS X (\d+[._]\d+)/.test(u)) os = 'macOS';
    else if (/iPhone|iPad/.test(u)) os = 'iOS';
    else if (/Android/.test(u)) os = 'Android';
    else if (/Linux/.test(u)) os = 'Linux';
    const b = version ? `${browser} ${version}` : browser;
    return os ? `${b} on ${os}` : b;
  }

  /** User-friendly IP display: "Localhost" for 127.0.0.1 / ::1, otherwise the actual IP */
  formatIpDisplay(ip: string | null | undefined): string {
    if (!ip || ip === '—') return '—';
    const t = ip.trim();
    if (t === '::1' || t === '127.0.0.1' || t.startsWith('::ffff:127.')) return 'Localhost';
    return ip;
  }

  /** Tooltip for IP when it's localhost (for accessibility) */
  ipTitle(ip: string | null | undefined): string {
    if (!ip) return '';
    const t = ip.trim();
    if (t === '::1' || t === '127.0.0.1') return ip;
    return '';
  }

  locationLabel(device: TrustedDeviceItem): string {
    const parts = [device.city, device.country].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }

  /** Whether location is empty because of local/private IP (for tooltip) */
  isLocationUnavailable(device: TrustedDeviceItem): boolean {
    const hasLocation = !!(device.city || device.country);
    if (hasLocation) return false;
    const ip = (device.ip_address || '').trim();
    return ip === '::1' || ip === '127.0.0.1' || ip.startsWith('192.168.') || ip.startsWith('10.');
  }

  expandedLoginId: number | null = null;
  /** When set, the expanded row with this login_id shows the technical details block */
  showTechnicalDetailsLoginId: number | null = null;

  toggleDetails(device: TrustedDeviceItem) {
    this.expandedLoginId = this.expandedLoginId === device.login_id ? null : device.login_id;
    this.showTechnicalDetailsLoginId = null;
    if (device && this.isCurrentDevice(device)) this.ensureClientDeviceDetails();
  }

  toggleTechnicalDetails(device: TrustedDeviceItem) {
    this.showTechnicalDetailsLoginId = this.showTechnicalDetailsLoginId === device.login_id ? null : device.login_id;
  }

  isTechnicalDetailsShown(device: TrustedDeviceItem): boolean {
    return this.showTechnicalDetailsLoginId === device.login_id;
  }

  isExpanded(device: TrustedDeviceItem): boolean {
    return this.expandedLoginId === device.login_id;
  }

  /** Trust expires at date (trusted_at + 30 days) for display */
  trustExpiresAt(device: TrustedDeviceItem): string | null {
    return device.trust_expires_at || null;
  }

  /** Days until trust expires (positive number or 0) */
  daysUntilTrustExpires(device: TrustedDeviceItem): number | null {
    if (!device.trust_expires_at || !device.is_trusted) return null;
    const expiry = new Date(device.trust_expires_at);
    const now = new Date();
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    return Math.max(0, days);
  }

  /** Short session fingerprint for display (last 16 chars) */
  sessionFingerprintShort(device: TrustedDeviceItem): string | null {
    const fp = device.token_fingerprint;
    if (!fp || typeof fp !== 'string') return null;
    return fp.length > 16 ? `…${fp.slice(-16)}` : fp;
  }

  sessionStatus(device: TrustedDeviceItem): 'active' | 'ended' {
    return device.is_active && !device.logged_out_at ? 'active' : 'ended';
  }

  /** Client-side device/browser details (only meaningful for current device) */
  clientDeviceDetails: ClientDeviceDetails | null = null;

  /** Collect device & browser info from the current browser (run once when needed) */
  ensureClientDeviceDetails(): void {
    if (this.clientDeviceDetails) return;
    const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
    const win = typeof window !== 'undefined' ? window : ({} as Window);
    const screen = (win as any).screen || {};
    const connection = (nav as any).connection || (nav as any).mozConnection || (nav as any).webkitConnection;
    this.clientDeviceDetails = {
      screenWidth: screen.width ?? null,
      screenHeight: screen.height ?? null,
      availWidth: screen.availWidth ?? null,
      availHeight: screen.availHeight ?? null,
      colorDepth: screen.colorDepth ?? null,
      pixelRatio: (win as any).devicePixelRatio ?? null,
      innerWidth: (win as any).innerWidth ?? null,
      innerHeight: (win as any).innerHeight ?? null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
      timezoneOffset: new Date().getTimezoneOffset(),
      language: nav.language ?? null,
      languages: Array.isArray(nav.languages) ? nav.languages.join(', ') : (nav.language || null),
      platform: nav.platform ?? null,
      vendor: nav.vendor ?? null,
      hardwareConcurrency: (nav as any).hardwareConcurrency ?? null,
      deviceMemory: (nav as any).deviceMemory ?? null,
      maxTouchPoints: (nav as any).maxTouchPoints ?? null,
      cookieEnabled: nav.cookieEnabled ?? null,
      doNotTrack: (nav as any).doNotTrack ?? null,
      onLine: (nav as any).onLine ?? null,
      connectionType: connection?.effectiveType ?? connection?.type ?? null,
      connectionDownlink: connection?.downlink ?? null,
      connectionRtt: connection?.rtt ?? null,
    };
  }

  /** Keys and labels for client details (single array ref to avoid change-detection loops in *ngFor) */
  readonly clientDetailKeys: { key: keyof ClientDeviceDetails; labelKey: string }[] = [
    { key: 'screenWidth', labelKey: 'TrustedDevices.clientScreenSize' },
    { key: 'availWidth', labelKey: 'TrustedDevices.clientAvailSize' },
    { key: 'colorDepth', labelKey: 'TrustedDevices.clientColorDepth' },
    { key: 'pixelRatio', labelKey: 'TrustedDevices.clientPixelRatio' },
    { key: 'innerWidth', labelKey: 'TrustedDevices.clientViewport' },
    { key: 'timezone', labelKey: 'TrustedDevices.clientTimezone' },
    { key: 'timezoneOffset', labelKey: 'TrustedDevices.clientTimezoneOffset' },
    { key: 'language', labelKey: 'TrustedDevices.clientLanguage' },
    { key: 'languages', labelKey: 'TrustedDevices.clientLanguages' },
    { key: 'platform', labelKey: 'TrustedDevices.clientPlatform' },
    { key: 'vendor', labelKey: 'TrustedDevices.clientVendor' },
    { key: 'hardwareConcurrency', labelKey: 'TrustedDevices.clientCpuCores' },
    { key: 'deviceMemory', labelKey: 'TrustedDevices.clientDeviceMemory' },
    { key: 'maxTouchPoints', labelKey: 'TrustedDevices.clientMaxTouchPoints' },
    { key: 'cookieEnabled', labelKey: 'TrustedDevices.clientCookies' },
    { key: 'doNotTrack', labelKey: 'TrustedDevices.clientDoNotTrack' },
    { key: 'onLine', labelKey: 'TrustedDevices.clientOnline' },
    { key: 'connectionType', labelKey: 'TrustedDevices.clientConnectionType' },
    { key: 'connectionDownlink', labelKey: 'TrustedDevices.clientDownlink' },
    { key: 'connectionRtt', labelKey: 'TrustedDevices.clientRtt' },
  ];

  trackByClientDetailKey(_index: number, item: { key: keyof ClientDeviceDetails; labelKey: string }): string {
    return item.key;
  }

  formatClientDetailValue(key: keyof ClientDeviceDetails, value: any): string {
    if (value == null) return '—';
    if (key === 'timezoneOffset') return `${value} min (UTC${value <= 0 ? '+' : ''}${-value / 60})`;
    if (key === 'deviceMemory') return `${value} GB`;
    if (key === 'connectionDownlink') return `${value} Mbps`;
    if (key === 'connectionRtt') return `${value} ms`;
    if (key === 'pixelRatio' && typeof value === 'number') return value.toFixed(2);
    if (key === 'innerWidth' && this.clientDeviceDetails?.innerHeight != null) return `${value} × ${this.clientDeviceDetails.innerHeight}`;
    if (key === 'screenWidth' && this.clientDeviceDetails?.screenHeight != null) return `${value} × ${this.clientDeviceDetails.screenHeight}`;
    if (key === 'availWidth' && this.clientDeviceDetails?.availHeight != null) return `${value} × ${this.clientDeviceDetails.availHeight}`;
    if (key === 'platform' && typeof value === 'string') return this.friendlyPlatformName(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  private friendlyPlatformName(platform: string): string {
    const p = (platform || '').trim();
    if (/MacIntel|Macintosh/i.test(p)) return 'macOS (Intel)';
    if (/Win32|Win64|Windows/i.test(p)) return 'Windows';
    if (/Linux/i.test(p)) return 'Linux';
    if (/iPhone|iPad/i.test(p)) return 'iOS';
    return p || '—';
  }
}

/** Device/browser details collected from the client (current device only) */
export interface ClientDeviceDetails {
  screenWidth: number | null;
  screenHeight: number | null;
  availWidth: number | null;
  availHeight: number | null;
  colorDepth: number | null;
  pixelRatio: number | null;
  innerWidth: number | null;
  innerHeight: number | null;
  timezone: string | null;
  timezoneOffset: number;
  language: string | null;
  languages: string | null;
  platform: string | null;
  vendor: string | null;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  maxTouchPoints: number | null;
  cookieEnabled: boolean | null;
  doNotTrack: string | null;
  onLine: boolean | null;
  connectionType: string | null;
  connectionDownlink: number | null;
  connectionRtt: number | null;
}
